import { useState } from 'react'
import { planCardSync, type LocalDeckCard } from '@/hooks/decks/deckBuilderCards'
import {
  useCreateDeck,
  useAddCardToDeck,
  useUpdateDeckCard,
  useRemoveDeckCard,
  useUpdateDeck,
} from '@/hooks/decks/useDecks'
import { ApiError } from '@/services/api'

interface DeckSaveInput {
  deckId?: number
  isEditing: boolean
  name: string
  description: string
  leaderId: number | null
  isPublic: boolean
  localCards: LocalDeckCard[]
  serverCardsSnapshot: LocalDeckCard[]
  setServerCardsSnapshot: (cards: LocalDeckCard[]) => void
  setErrors: (errors: { name?: string }) => void
  updateDeck: ReturnType<typeof useUpdateDeck>
}

/**
 * Surface a 422 validation failure (e.g. a duplicate deck name) inline on
 * the name field, then return null to signal "handled — stop the save".
 * Re-throw anything else so genuine failures still surface globally. This
 * keeps the error off the FailedMutationsSheet, where it landed as an
 * "Unknown change" with a Retry that could never succeed (#204). Scoped to
 * JUST the create/update metadata call so an unrelated failure (e.g. a card
 * sync) can't be mislabeled as a name error.
 *
 * Every 422 maps to the (only) name field on purpose: the create/update
 * meta carries inlineErrorStatuses:[422], so the global handler suppresses
 * the 422 toast expecting the form to show it. If we narrowed this to
 * name-only messages and re-threw the rest, a hypothetical non-name 422
 * would be swallowed globally AND not shown here — silent. Name is the deck
 * form's only validated text field today, so this is exact; if a non-name
 * validation is ever added, it will still be VISIBLE here (just under the
 * name field) rather than lost.
 */
function nameValidationHandler(setErrors: (errors: { name?: string }) => void) {
  return (err: unknown): null => {
    if (err instanceof ApiError && err.status === 422) {
      setErrors({ name: err.message })
      return null
    }
    throw err
  }
}

/** Creates/updates the deck and syncs its cards. `handleSave` returns the new deck id on create, undefined otherwise. */
export function useDeckSave(input: DeckSaveInput) {
  const createDeck = useCreateDeck()
  const addCard = useAddCardToDeck()
  const updateCard = useUpdateDeckCard()
  const removeCard = useRemoveDeckCard()
  const [isSaving, setIsSaving] = useState(false)

  const metadata = () => ({
    name: input.name.trim(),
    description: input.description.trim() || undefined,
    leaderCardId: input.leaderId as number,
  })

  const createNew = async (onInvalid: (err: unknown) => null): Promise<number | undefined> => {
    const created = await createDeck.mutateAsync({ data: { ...metadata(), isPublic: input.isPublic } }).catch(onInvalid)
    if (created === null) return undefined
    for (const lc of input.localCards) {
      await addCard.mutateAsync({ deckId: created.id, cardId: lc.card.id, quantity: lc.quantity })
    }
    return created.id
  }

  const updateExisting = async (deckId: number, onInvalid: (err: unknown) => null): Promise<void> => {
    const updated = await input.updateDeck.mutateAsync({ id: deckId, data: metadata() }).catch(onInvalid)
    if (updated === null) return

    // Sync cards: compare local vs server snapshot
    const { removals, changes } = planCardSync(input.serverCardsSnapshot, input.localCards)
    for (const cardId of removals) {
      await removeCard.mutateAsync({ deckId, cardId })
    }
    for (const { kind, cardId, quantity } of changes) {
      if (kind === 'add') await addCard.mutateAsync({ deckId, cardId, quantity })
      else await updateCard.mutateAsync({ deckId, cardId, quantity })
    }
    input.setServerCardsSnapshot([...input.localCards])
  }

  const handleSave = async (): Promise<number | undefined> => {
    if (!input.name.trim()) {
      input.setErrors({ name: 'Deck name is required' })
      return undefined
    }
    if (!input.leaderId) return undefined

    input.setErrors({})
    setIsSaving(true)

    try {
      const onInvalid = nameValidationHandler(input.setErrors)
      if (!input.isEditing) return await createNew(onInvalid)
      if (input.deckId) await updateExisting(input.deckId, onInvalid)
      return undefined
    } finally {
      setIsSaving(false)
    }
  }

  return { isSaving, handleSave }
}
